import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FileUpload from "@/components/admin/FileUpload";
import { FileGrid } from "@/components/admin/PrivateFile";
import { fileUrl } from "@/lib/api";
import {
  money, merchCategoryLabel, MERCH_CATEGORIES, TIER_LABELS, TIER_ORDER, can, MANAGE_MERCH,
} from "@/lib/format";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Store, Star, Crown, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  name: "", category: "apparel", price: "", compare_at_price: "", inventory_count: 0, sku: "",
  short_description: "", description: "", images: [], is_active: true, featured: false,
  vip_only: false, vip_min_tier: "patron_client",
};

export default function AdminMerch() {
  const { user } = useAdminAuth();
  const editable = can(user?.role, MANAGE_MERCH);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/merch");
      setItems(r.data);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => setForm({ ...EMPTY });
  const openEdit = (p) => setForm({ ...EMPTY, ...p });

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.price === "" || isNaN(Number(form.price))) { toast.error("Valid price is required"); return; }
    const payload = {
      ...form,
      price: Number(form.price),
      compare_at_price: form.compare_at_price === "" || form.compare_at_price === null ? null : Number(form.compare_at_price),
      inventory_count: Number(form.inventory_count) || 0,
      vip_min_tier: form.vip_only ? form.vip_min_tier : null,
    };
    try {
      if (form.id) await api.put(`/admin/merch/${form.id}`, payload);
      else await api.post("/admin/merch", payload);
      toast.success(form.id ? "Product updated" : "Product created");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const toggleVisibility = (p) =>
    act(() => api.patch(`/admin/merch/${p.id}/visibility`, { is_active: !p.is_active }), p.is_active ? "Product hidden" : "Product published");
  const toggleFeatured = (p) =>
    act(() => api.patch(`/admin/merch/${p.id}/featured`, { featured: !p.featured }), p.featured ? "Removed from featured" : "Marked featured");
  const doDelete = () =>
    act(() => api.delete(`/admin/merch/${toDelete.id}`), "Product deleted").then(() => setToDelete(null));

  return (
    <div data-testid="admin-merch">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Store className="text-[#c9a86a]" size={22} />
          <div>
            <h1 className="text-2xl font-semibold">Merch Store</h1>
            <p className="text-sm text-[var(--text-2)]">{items.length} products</p>
          </div>
        </div>
        {editable && (
          <Button onClick={openNew} data-testid="add-merch-btn" className="bg-[var(--panel-2)] hover:bg-[var(--border-2)] text-[#c9a86a]">
            <Plus size={16} className="mr-2" /> Add Product
          </Button>
        )}
      </div>

      <Card className="border-[var(--border-lch)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--panel-2)] text-[var(--text-2)] text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">Product</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Price</th>
                <th className="text-left p-3">Stock</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Flags</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-[var(--border-lch)] hover:bg-[var(--panel-2)]/50" data-testid={`merch-row-${p.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded bg-[var(--panel-2)] overflow-hidden flex items-center justify-center shrink-0">
                        {p.images?.[0] ? <img src={fileUrl(p.images[0].url)} alt="" className="h-full w-full object-cover" /> : <Store size={16} className="text-[var(--muted)]" />}
                      </div>
                      <div>
                        <button onClick={() => editable ? openEdit(p) : null} className="font-medium text-left hover:text-[#9a7b3f]">{p.name}</button>
                        <div className="text-xs text-[var(--muted)]">{p.sku || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-[var(--text-2)]">{merchCategoryLabel(p.category)}</td>
                  <td className="p-3 text-[var(--text)]">{money(p.price)}</td>
                  <td className="p-3"><span className={p.inventory_count > 0 ? (p.inventory_count <= 5 ? "text-amber-600" : "text-[var(--text)]") : "text-red-600"}>{p.inventory_count}</span></td>
                  <td className="p-3">
                    <Badge variant="outline" className={p.is_active ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-[var(--panel-2)] text-[var(--text-2)] border-[var(--border-lch)]"}>
                      {p.is_active ? "Published" : "Hidden"}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5">
                      {p.featured && <Star size={14} className="text-[#c9a86a] fill-[#c9a86a]" />}
                      {p.vip_only && <Crown size={14} className="text-[#9a7b3f]" />}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    {editable ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`merch-actions-${p.id}`}><MoreHorizontal size={18} /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => openEdit(p)}>Edit Product</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleFeatured(p)}>{p.featured ? "Remove Featured" : "Mark Featured"}</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleVisibility(p)}>{p.is_active ? <><EyeOff size={14} className="mr-2" />Hide</> : <><Eye size={14} className="mr-2" />Publish</>}</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600" onClick={() => setToDelete(p)}>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : <span className="text-xs text-[var(--muted)]">View only</span>}
                  </td>
                </tr>
              ))}
              {!loading && !items.length && (
                <tr><td colSpan={7} className="p-8 text-center text-[var(--muted)]">No products yet. Add your first product.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {form && (
            <>
              <DialogHeader><DialogTitle>{form.id ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[var(--panel)]" data-testid="merch-form-name" /></div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Category</Label>
                    <Select value={form.category} onValueChange={(v) => set("category", v)}>
                      <SelectTrigger className="bg-[var(--panel)]" data-testid="merch-form-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{MERCH_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">SKU</Label><Input value={form.sku ?? ""} onChange={(e) => set("sku", e.target.value)} className="bg-[var(--panel)]" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Price (USD)</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-[var(--panel)]" data-testid="merch-form-price" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Compare-at Price (optional)</Label><Input type="number" value={form.compare_at_price ?? ""} onChange={(e) => set("compare_at_price", e.target.value)} className="bg-[var(--panel)]" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Inventory Count</Label><Input type="number" value={form.inventory_count} onChange={(e) => set("inventory_count", e.target.value)} className="bg-[var(--panel)]" data-testid="merch-form-inventory" /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Short Description</Label><Input value={form.short_description ?? ""} onChange={(e) => set("short_description", e.target.value)} className="bg-[var(--panel)]" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Full Description</Label><Textarea rows={3} value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} className="bg-[var(--panel)]" /></div>

                <div>
                  <Label className="text-xs text-[var(--text-2)]">Product Images</Label>
                  <div className="my-2"><FileGrid files={form.images} onRemove={(f) => set("images", (form.images || []).filter((x) => x.id !== f.id))} /></div>
                  <FileUpload visibility="public" category="merch" label="Add Images" onUploaded={(fs) => set("images", [...(form.images || []), ...fs])} testId="merch-image-upload" />
                </div>

                <div className="grid sm:grid-cols-3 gap-4 border-t border-[var(--border-lch)] pt-4">
                  <div className="flex items-center justify-between"><Label className="text-xs">Published</Label><Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="merch-form-active" /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Featured</Label><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="merch-form-featured" /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">VIP Only</Label><Switch checked={form.vip_only} onCheckedChange={(v) => set("vip_only", v)} data-testid="merch-form-vip" /></div>
                </div>
                {form.vip_only && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Minimum VIP Tier</Label>
                    <Select value={form.vip_min_tier || "patron_client"} onValueChange={(v) => set("vip_min_tier", v)}>
                      <SelectTrigger className="bg-[var(--panel)]" data-testid="merch-form-tier"><SelectValue /></SelectTrigger>
                      <SelectContent>{TIER_ORDER.map((t) => <SelectItem key={t} value={t}>{TIER_LABELS[t]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={save} data-testid="merch-save" className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]">{form.id ? "Save Changes" : "Create Product"}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.name} will be permanently removed from the store. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="merch-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
